import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

/**
 * Keeping a long list cheap to render.
 *
 * "Show all" on a list page can put hundreds of rows on screen, and every row
 * here carries inline editors, pickers and icon buttons. Mounting all of them
 * at once froze `/companies` (2026-10-04). `useRowWindow` mounts only the rows
 * near the visible part of the list; a spacer above and below the rendered
 * rows stands in for the rest, so the scrollbar keeps the full list's height.
 *
 * It works the same for a table inside its own scroll area (`TableWrap
 * frozenHeader`) and for a list that scrolls with the page (the phone cards):
 * the visible band is the part of the viewport the nearest scrolling ancestor
 * actually shows, so neither case needs to be told which it is.
 *
 * ⚠️ A row scrolled far out of the window is unmounted, so anything held only
 * in that row's own state (an inline edit somebody typed and then scrolled
 * away from without leaving the field) is dropped. The overscan keeps that to
 * rows well off screen.
 */

/** Whether a media query matches, read synchronously so the first paint is right. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/**
 * The nearest ancestor that is really scrolling its content vertically, or
 * `null` when the page itself is the scroller. An element whose `overflow-x` is
 * hidden computes `overflow-y: auto` without scrolling anything, hence the
 * height test.
 */
function scrollParentOf(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export interface RowWindow {
  /** First row index to render. */
  start: number;
  /** One past the last row index to render. */
  end: number;
  /** Height of the spacer above the rendered rows, in px. */
  padTop: number;
  /** Height of the spacer below the rendered rows, in px. */
  padBottom: number;
  /** Ref for the TOP spacer element; the rendered rows must follow it directly. */
  topSpacerRef: (element: HTMLElement | null) => void;
}

/**
 * @param count    how many rows the list holds
 * @param enabled  false renders every row (a short page needs no window)
 * @param resetKey change it when the SET of rows changes, so heights measured
 *   for the old list are not reused for the new one
 */
export function useRowWindow(
  count: number,
  enabled: boolean,
  resetKey: unknown,
  estimate = 56,
  overscan = 12,
): RowWindow {
  // A first batch before anything is measured; the output is clamped to count.
  // The spacer heights live in state with the range, never read off a ref
  // during render.
  const [win, setWin] = useState({ start: 0, end: 30, padTop: 0, padBottom: 0 });
  const [spacer, setSpacer] = useState<HTMLElement | null>(null);
  const heights = useRef<number[]>([]);
  const frame = useRef(0);

  useEffect(() => {
    heights.current = [];
  }, [resetKey]);

  const recompute = useCallback(() => {
    if (!spacer) return;
    const heightOf = (index: number) => heights.current[index] ?? estimate;

    // The band of the viewport the list can be seen through, in viewport px.
    const scroller = scrollParentOf(spacer);
    let bandTop = 0;
    let bandBottom = window.innerHeight;
    if (scroller) {
      const rect = scroller.getBoundingClientRect();
      bandTop = Math.max(bandTop, rect.top);
      bandBottom = Math.min(bandBottom, rect.bottom);
    }
    // The top spacer is the list's first element, so its top edge is the
    // list's top edge; everything below is measured from it.
    const listTop = spacer.getBoundingClientRect().top;
    const viewTop = bandTop - listTop;
    const viewBottom = Math.max(viewTop, bandBottom - listTop);

    let offset = 0;
    let first = 0;
    while (first < count && offset + heightOf(first) < viewTop) {
      offset += heightOf(first);
      first += 1;
    }
    let last = first;
    while (last < count && offset < viewBottom) {
      offset += heightOf(last);
      last += 1;
    }
    setWin((current) => {
      // Keep the rendered range while it still covers what is on screen with
      // some margin, so small measurement changes do not reshuffle the rows.
      const keep =
        current.end <= count &&
        current.start <= Math.max(0, first - 2) &&
        current.end >= Math.min(count, last + 2) &&
        current.end - current.start <= last - first + overscan * 3;
      const from = keep ? current.start : Math.max(0, first - overscan);
      const to = keep ? current.end : Math.min(count, Math.max(last + overscan, from + 1));
      let padTop = 0;
      let padBottom = 0;
      for (let index = 0; index < from; index += 1) padTop += heightOf(index);
      for (let index = to; index < count; index += 1) padBottom += heightOf(index);
      return current.start === from &&
        current.end === to &&
        current.padTop === padTop &&
        current.padBottom === padBottom
        ? current
        : { start: from, end: to, padTop, padBottom };
    });
  }, [spacer, count, estimate, overscan]);

  // Any scroll on the page — the table's own area or the window — moves the
  // band, so listen in the capture phase rather than on one element.
  useEffect(() => {
    if (!enabled || !spacer) return;
    const schedule = () => {
      window.cancelAnimationFrame(frame.current);
      frame.current = window.requestAnimationFrame(recompute);
    };
    schedule();
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(frame.current);
      document.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
    };
  }, [enabled, spacer, recompute]);

  const start = enabled ? Math.min(win.start, count) : 0;
  const end = enabled ? Math.min(Math.max(win.end, start), count) : count;

  /*
   * Measure the rows that rendered, so the spacers converge on real heights.
   *
   * 🔴 **Measured once, in whole pixels, and NEVER a synchronous update here.**
   * The first cut re-measured every rendered row on every commit and called
   * `recompute` straight from this effect. Heights that depend on which rows
   * are rendered made every commit "change" a height, set state and commit
   * again, until React gave up with error #185 and the whole of /companies
   * went to the error screen (2026-10-04). Deferring to the next frame means
   * this hook can never nest updates inside a commit, whatever the
   * measurements do.
   */
  useLayoutEffect(() => {
    if (!enabled || !spacer) return;
    let changed = false;
    let row = spacer.nextElementSibling as HTMLElement | null;
    for (let index = start; index < end && row; index += 1) {
      const height = Math.round(row.getBoundingClientRect().height);
      const previous = heights.current[index];
      // Each row is measured ONCE per set of rows. This table sizes its columns
      // from the rows on screen, so a different window means different widths,
      // different wrapping and different heights — re-measuring would let the
      // window chase its own tail. Once-only bounds the updates by the row
      // count; the cost is a spacer a few px off after a row grows in place.
      if (height > 0 && previous === undefined) {
        heights.current[index] = height;
        changed = true;
      }
      row = row.nextElementSibling as HTMLElement | null;
    }
    if (changed) {
      window.cancelAnimationFrame(frame.current);
      frame.current = window.requestAnimationFrame(recompute);
    }
  });

  return {
    start,
    end,
    padTop: enabled ? win.padTop : 0,
    padBottom: enabled ? win.padBottom : 0,
    topSpacerRef: setSpacer,
  };
}
